import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle, XCircle, Trophy, Calendar, User,
  FileText, Loader2, Shield, ExternalLink, Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function VerifyRecognitionPage() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    verifyRecognition();
  }, [code]);

  const verifyRecognition = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/recognition/recognitions/verify/${code}`);
      setData(response.data);
    } catch (err) {
      setError('Error verificando el reconocimiento');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-900 via-amber-800 to-yellow-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-amber-400 mx-auto mb-4" />
          <p className="text-amber-200">Verificando reconocimiento...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-900 via-amber-800 to-yellow-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error de Verificación</h1>
          <p className="text-gray-600 mb-6">{error || 'No se pudo verificar el reconocimiento'}</p>
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700"
          >
            Ir al inicio
          </Link>
        </div>
      </div>
    );
  }

  const { valid, recognition } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-900 via-amber-800 to-yellow-900 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/30 text-amber-200 rounded-full text-sm font-medium mb-4">
            <Shield className="w-4 h-4" />
            Sistema de Verificación UPIM
          </div>
          <h1 className="text-3xl font-bold text-white">Verificación de Reconocimiento</h1>
        </div>

        {/* Result Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Status Banner */}
          <div className={`p-6 ${valid ? 'bg-gradient-to-r from-amber-500 to-yellow-500' : 'bg-gradient-to-r from-red-500 to-rose-600'}`}>
            <div className="flex items-center justify-center gap-3">
              {valid ? (
                <>
                  <Trophy className="w-12 h-12 text-white" />
                  <div className="text-white">
                    <p className="text-sm font-medium opacity-90">Estado</p>
                    <p className="text-2xl font-bold">RECONOCIMIENTO VÁLIDO</p>
                  </div>
                </>
              ) : (
                <>
                  <XCircle className="w-12 h-12 text-white" />
                  <div className="text-white">
                    <p className="text-sm font-medium opacity-90">Estado</p>
                    <p className="text-2xl font-bold">RECONOCIMIENTO NO VÁLIDO</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {valid && recognition && (
            <div className="p-8">
              {/* Holder Info */}
              <div className="mb-8 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full mx-auto mb-4 flex items-center justify-center shadow-lg">
                  <Trophy className="w-10 h-10 text-white" />
                </div>
                <p className="text-sm text-gray-500 mb-1">Otorgado a</p>
                <p className="text-2xl font-bold text-gray-900 mb-2">{recognition.holder_name}</p>
                <div className="inline-block bg-amber-100 text-amber-800 px-4 py-2 rounded-full">
                  <span className="font-mono font-bold tracking-wider">
                    {recognition.affiliation_number}
                  </span>
                </div>
              </div>

              {/* Recognition Info */}
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-6 mb-6">
                <div className="text-center">
                  <p className="text-xs text-amber-600 uppercase tracking-widest mb-2">Reconocimiento</p>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{recognition.program_name}</h3>
                  <p className="text-amber-700 font-medium">{recognition.recognition_title}</p>
                </div>
                
                <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-amber-200">
                  <Star className="w-5 h-5 text-amber-500" />
                  <span className="text-gray-700">
                    <strong>{recognition.total_constancias}</strong> constancias completadas
                  </span>
                </div>
              </div>

              {/* Date */}
              <div className="flex items-center justify-center gap-2 text-gray-500 mb-6">
                <Calendar className="w-4 h-4" />
                <span>Otorgado el {formatDate(recognition.issued_at)}</span>
              </div>

              {/* Verification Code */}
              <div className="text-center pt-6 border-t border-gray-100">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Código de verificación</p>
                <p className="text-lg font-mono font-bold text-amber-600 tracking-widest">{code}</p>
              </div>
            </div>
          )}

          {!valid && (
            <div className="p-8 text-center">
              <p className="text-gray-600 mb-4">
                El reconocimiento con el código <strong className="font-mono">{code}</strong> no fue encontrado o no es válido.
              </p>
              <p className="text-sm text-gray-500">
                Si crees que esto es un error, por favor contacta a soporte.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-amber-200 text-sm mb-4">
            Verificación oficial del sistema UPIM
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-amber-300 hover:text-amber-200"
          >
            <ExternalLink className="w-4 h-4" />
            Visitar UPIM Academia
          </Link>
        </div>
      </div>
    </div>
  );
}
